"use client";

import type React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import type { Chat } from "../../types/chat";

interface ChatCardProps {
  chat: Chat;
}

const ChatCard: React.FC<ChatCardProps> = ({ chat }) => {
  const lastEditing = chat.editings[chat.editings.length - 1];
  const lastMessage = lastEditing?.request || "No messages yet";
  const lastImage = lastEditing?.generatedImageUrl || chat.originalImageUrls[0];
  const time = lastEditing?.date?.toDate()?.toLocaleTimeString() || "";
  const editingCount = chat.editings.length;

  return (
    <Link to={`/interactive-editing/${chat.id}`}>
      <motion.div
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
        className="group relative flex items-center p-5 rounded-2xl bg-white/5 border border-white/10 hover:border-white/20 cursor-pointer backdrop-blur-xl shadow-2xl hover:shadow-3xl transition-all duration-500 overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-20 h-20 opacity-30">
          <div className="absolute top-2 right-2 w-3 h-3 border-t-2 border-r-2 border-cyan-400/60"></div>
          <div className="absolute top-4 right-4 w-2 h-2 bg-cyan-400/40 rounded-full"></div>
          <div className="absolute top-1 right-6 w-6 h-px bg-gradient-to-r from-transparent to-cyan-400/50"></div>
          <div className="absolute top-6 right-1 w-px h-6 bg-gradient-to-b from-transparent to-cyan-400/50"></div>
        </div>

        <div className="absolute bottom-0 left-0 w-20 h-20 opacity-30">
          <div className="absolute bottom-2 left-2 w-3 h-3 border-b-2 border-l-2 border-blue-400/60"></div>
          <div className="absolute bottom-4 left-4 w-2 h-2 bg-blue-400/40 rounded-full"></div>
          <div className="absolute bottom-1 left-6 w-6 h-px bg-gradient-to-l from-transparent to-blue-400/50"></div>
          <div className="absolute bottom-6 left-1 w-px h-6 bg-gradient-to-t from-transparent to-blue-400/50"></div>
        </div>

        <div className="relative flex-shrink-0 mr-4">
          <img
            src={
              lastImage ||
              "https://plus.unsplash.com/premium_photo-1673697239981-389164b7b87f?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8bmF0dXJlfGVufDB8fDB8fHww"
            }
            alt={chat.title}
            className="w-16 h-16 rounded-xl object-cover shadow-md group-hover:shadow-lg transition-shadow duration-300"
          />
          <div className="absolute inset-0 rounded-xl bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>

        <div className="flex-1 min-w-0 pr-3">
          <div className="flex justify-between items-start mb-1">
            <h3 className="font-semibold text-white/90 text-base leading-tight truncate pr-2">
              {chat.title}
            </h3>
            <span className="text-xs text-white/60 font-medium whitespace-nowrap ml-2">
              {time}
            </span>
          </div>
          <p className="text-sm text-white/70 leading-relaxed line-clamp-2 break-words">
            {lastMessage}
          </p>
        </div>

        <div className="flex flex-col items-end justify-center ml-3 flex-shrink-0">
          <div className="bg-white/10 backdrop-blur-sm px-2 py-1 rounded-lg border border-white/20">
            <span className="text-xs text-white/80 font-medium">
              {editingCount} {editingCount === 1 ? "edit" : "edits"}
            </span>
          </div>
        </div>
      </motion.div>
    </Link>
  );
};

export default ChatCard;
